import React, { Dispatch, SetStateAction, useEffect } from 'react'
import { Container, DetailsContainer, MessageError } from './styles'
import { TestPropertiesList } from '../TestPropertiesList'
import { LaboratoryTestPropertiesComparison } from '../../LaboratoryTestPropertiesComparison'
import { HealthProcedureDetails } from '~/components/healthProcedures/healthProcedureDetails'
import { useLaboratoryTest } from '~/context'
import { IHealthProcedures, ILaboratoryTest } from '~/interfaces'
import { hasObjectValue } from '~/utils'
import { ModalHeader } from '~/components/healthProcedures/ModalComponents/ModalHeader'
import { ReferringPhysicianDetails } from '~/components/healthProcedures/ReferringPhysicianDetails'
import { DataNotFound } from '~/components/shared'
import { ModalHeaderSkeleton } from '~/components/healthProcedures/ModalComponents/ModalHeader/Skeleton'

interface ContentProps {
  patientId: number
  healthProcedure: IHealthProcedures
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
}

export const Content = ({ showModal, setShowModal, patientId, healthProcedure }: ContentProps): JSX.Element => {
  const {
    isLoading,
    laboratoryTest,
    getLaboratoryTest,
    getLaboratoryTestProperties,
    errorLaboratoryTest,
    openLaboratoryTestPropertiesComparison,
    setOpenLaboratoryTestPropertiesComparison
  } = useLaboratoryTest()

  useEffect(() => {
    getLaboratoryTest({ testId: healthProcedure.id })
    getLaboratoryTestProperties({ pathParams: { testId: healthProcedure.id } })
  }, [getLaboratoryTest, getLaboratoryTestProperties, healthProcedure.id, patientId])

  const renderLoading = () => {
    return (
      <>
        <DetailsContainer>
          <ModalHeaderSkeleton />
        </DetailsContainer>
      </>
    )
  }

  const renderContent = (content: ILaboratoryTest) => {
    if (errorLaboratoryTest)
      return (
        <MessageError>
          <DataNotFound text={errorLaboratoryTest} />
        </MessageError>
      )

    if (hasObjectValue({ ...content })) {
      return (
        <>
          <ModalHeader title={content?.description} datetime={content.startDate} />
          <DetailsContainer>
            <ReferringPhysicianDetails physician={content.referringPhysician} />
            <TestPropertiesList />
          </DetailsContainer>
        </>
      )
    }
  }

  return (
    <>
      <HealthProcedureDetails showModal={showModal} setShowModal={setShowModal} viewType="SideModal">
        <Container>{isLoading ? renderLoading() : renderContent(laboratoryTest)}</Container>
      </HealthProcedureDetails>

      {openLaboratoryTestPropertiesComparison && (
        <LaboratoryTestPropertiesComparison
          showModal={openLaboratoryTestPropertiesComparison}
          setShowModal={setOpenLaboratoryTestPropertiesComparison}
          patientId={patientId}
        />
      )}
    </>
  )
}
